import { useEffect, useLayoutEffect, useState } from 'react';
import { Order } from '../../../clients/quipli-service/order/order';
import { Button } from '../../../components/form/button';
import { Modal } from '../../../components/modal/modal';
import { Spinner } from '../../../components/spinner/spinner';
import { toast } from '../../../util/toasts';
import { Card } from '../../../components/card/card';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { DateTimePicker } from '../../../components/date-picker/date-time-picker';
import { DateTime, Interval } from 'luxon';
import { Collapsible } from '../../../components/collapsible/collapsible';
import { useYorentClient } from '../../../clients/yorent/yorent-client';
import { ProductTrackingType } from '../../../clients/quipli-service/product/product';

type ProductBufferPeriod = {
  name: string;
  start: DateTime;
  end?: DateTime;
  before: DateTime;
  after?: DateTime;
  isActive: boolean;
  hasErrors: boolean;
};
type BufferPeriodList = { [orderProductId: number]: ProductBufferPeriod };

export const BufferPeriodModal = ({
  order,
  onSubmit,
  onClose,
}: {
  order: Order;
  onSubmit: () => void;
  onClose: () => void;
}) => {
  const { submitBufferPeriod } = useYorentClient();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [bufferPeriodList, setBufferPeriodList] = useState<BufferPeriodList>({});
  const [hasErrors, setHasErrors] = useState<boolean>(false);

  const handleClose = () => {
    if (isLoading) return;

    onClose();
  };

  const handleSubmit = async () => {
    if (isLoading || hasErrors) return;

    setIsLoading(true);
    const bufferPeriods = Object.entries(bufferPeriodList)
      .filter(([, { isActive }]) => isActive)
      .map(([id, item]) => {
        const { product, quantity, rentalStartDate, rentalEndDate } = order.products.find((p) => p.id === Number(id))!;

        return {
          opr_id: id,
          buffer_period_before_rental_date: item.before.toFormat('LL/dd/yyyy'),
          buffer_period_before_rental_time: item.before.toFormat('hh:mm+a'),
          buffer_period_after_rental_date: item.after?.toFormat('LL/dd/yyyy'),
          buffer_period_after_rental_time: item.after?.toFormat('hh:mm+a'),
          product_start_rental_time: rentalStartDate.toFormat('yyyy-LL-dd HH:mm:ss'),
          product_end_rental_time: rentalEndDate?.toFormat('yyyy-LL-dd HH:mm:ss'),
          prod_tracking_type: product.trackingType === ProductTrackingType.SerialUnits ? 1 : 0,
          opr_product_id: `${product.id}`,
          opr_order_id: `${order.id}`,
          qty: quantity,
        };
      });

    try {
      const { status, ...res } = await submitBufferPeriod({
        order_id: `${order.id}`,
        order_product_buffer: bufferPeriods,
      });

      if (status === 1) {
        toast('Buffer period updated.');
        onSubmit();
      } else {
        toast(res.msg || 'Something went wrong.', 'error');
      }
    } catch (error: any) {
      toast('Something went wrong.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useLayoutEffect(() => {
    const bufferPeriods: BufferPeriodList = {};
    order.products.forEach((item) => {
      bufferPeriods[item.id] = {
        name: item.product.name,
        start: item.rentalStartDate,
        end: item.rentalEndDate,
        before: item.rentalStartDate.minus({ minutes: item.bufferMinutesBeforeRental }),
        after: item.rentalEndDate?.plus({ minutes: item.bufferMinutesAfterRental }),
        isActive: true,
        hasErrors: false,
      };
    });

    setBufferPeriodList(bufferPeriods);
  }, []);

  useEffect(() => {
    setHasErrors(Object.values(bufferPeriodList).some((item) => item.hasErrors));
  }, [bufferPeriodList]);

  return (
    <Modal
      title="Buffer Period"
      className="!bg-grey-25"
      isOpen={true}
      close={handleClose}
      actions={
        isLoading ? (
          <Spinner />
        ) : (
          <>
            <Button className="button-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button className="button-yellow" onClick={handleSubmit} disabled={hasErrors}>
              Save
            </Button>
          </>
        )
      }
    >
      <div className="flex-flex-col gap-[8xp]">
        {order.products.map(({ id, rentalEndDate }) => (
          <BufferPeriodProduct
            key={`buffer-period-item-${id}`}
            bufferId={id}
            bufferItem={bufferPeriodList[id]}
            hasEndDate={rentalEndDate !== undefined}
            update={setBufferPeriodList}
          />
        ))}
      </div>
    </Modal>
  );
};

const BufferPeriodProduct = ({
  bufferId,
  bufferItem,
  hasEndDate,
  update,
}: {
  bufferId: number;
  bufferItem: ProductBufferPeriod;
  hasEndDate: boolean;
  update: React.Dispatch<React.SetStateAction<BufferPeriodList>>;
}) => {
  const [isChecked, setIsChecked] = useState<boolean>(true);

  const toggleBufferPeriod = (isActive: boolean) => {
    update((prev) => ({ ...prev, [bufferId]: { ...prev[bufferId], isActive } }));
  };

  const setBufferPeriod = (before: DateTime | null, after: DateTime | null) => {
    update((prev) => ({
      ...prev,
      [bufferId]: {
        ...bufferItem,
        before: before?.isValid ? before : bufferItem.before,
        after: after?.isValid ? after : bufferItem.after,
        hasErrors: before?.isValid === false || after?.isValid === false,
      },
    }));
  };

  return (
    <Card className="py-[14px] px-[12px] border border-solid border-grey-200 rounded-[8px]">
      <Collapsible
        openAtStart
        trigger={(isOpen) => (
          <Checkbox
            type="yellow"
            label={bufferItem.name}
            isChecked={isChecked && isOpen}
            onChange={(checkState) => {
              setIsChecked(checkState);
              toggleBufferPeriod(checkState);
            }}
          />
        )}
      >
        <div className="flex flex-col gap-[8px] mt-[8px]">
          <DateTimePicker
            usePortal
            label="Before buffer"
            value={bufferItem.before}
            disabledIntervals={[Interval.after(bufferItem.start.plus({ minutes: 30 }), { years: 10 })]}
            timezone={bufferItem.start.zoneName}
            onChange={(d) => d && setBufferPeriod(d, null)}
          />

          {hasEndDate && (
            <DateTimePicker
              usePortal
              label="After buffer"
              value={bufferItem.after}
              disabledIntervals={[Interval.before(bufferItem.end!, { years: 10 })]}
              timezone={bufferItem.end!.zoneName}
              onChange={(d) => d && setBufferPeriod(null, d)}
            />
          )}
        </div>
      </Collapsible>
    </Card>
  );
};
